import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const LightingControls = ({ lighting, onLightingChange, isVisible, onToggle }) => {
  const [activePreset, setActivePreset] = useState('studio');
  const [advancedMode, setAdvancedMode] = useState(false);

  // FIXED: Sync activePreset with incoming lighting.preset prop
  useEffect(() => {
    if (lighting?.preset) {
      setActivePreset(lighting?.preset);
    }
  }, [lighting?.preset]);

  const presets = [
    { 
      id: 'studio', 
      name: 'Studio', 
      icon: 'Lightbulb',
      settings: { intensity: 75, temperature: 5500, shadowSoftness: 50, environment: 30, ambientOcclusion: 40, showEnvironmentBackground: false }
    },
    { 
      id: 'sunset', 
      name: 'Sunset', 
      icon: 'Sunset',
      settings: { intensity: 60, temperature: 3500, shadowSoftness: 70, environment: 50, ambientOcclusion: 30, showEnvironmentBackground: true }
    },
    { 
      id: 'dawn', 
      name: 'Dawn', 
      icon: 'Sunrise',
      settings: { intensity: 50, temperature: 4500, shadowSoftness: 60, environment: 40, ambientOcclusion: 35, showEnvironmentBackground: true }
    },
    { 
      id: 'night', 
      name: 'Night', 
      icon: 'Moon',
      settings: { intensity: 30, temperature: 6500, shadowSoftness: 40, environment: 20, ambientOcclusion: 60, showEnvironmentBackground: true }
    },
    { 
      id: 'warehouse', 
      name: 'Warehouse', 
      icon: 'Warehouse',
      settings: { intensity: 85, temperature: 5000, shadowSoftness: 30, environment: 25, ambientOcclusion: 45, showEnvironmentBackground: true }
    },
    { 
      id: 'forest', 
      name: 'Forest', 
      icon: 'TreePine',
      settings: { intensity: 65, temperature: 5500, shadowSoftness: 80, environment: 70, ambientOcclusion: 50, showEnvironmentBackground: true }
    },
    { 
      id: 'apartment', 
      name: 'Apartment', 
      icon: 'Home',
      settings: { intensity: 70, temperature: 4000, shadowSoftness: 55, environment: 35, ambientOcclusion: 40, showEnvironmentBackground: true }
    },
    { 
      id: 'city', 
      name: 'City', 
      icon: 'Building',
      settings: { intensity: 80, temperature: 6000, shadowSoftness: 35, environment: 45, ambientOcclusion: 55, showEnvironmentBackground: true }
    },
    { 
      id: 'park', 
      name: 'Park', 
      icon: 'Trees',
      settings: { intensity: 75, temperature: 5800, shadowSoftness: 65, environment: 60, ambientOcclusion: 45, showEnvironmentBackground: true }
    },
    { 
      id: 'lobby', 
      name: 'Lobby', 
      icon: 'DoorOpen',
      settings: { intensity: 70, temperature: 4800, shadowSoftness: 45, environment: 40, ambientOcclusion: 50, showEnvironmentBackground: true }
    }
  ];

  const handlePresetChange = (preset) => {
    onLightingChange({
      ...preset?.settings,
      preset: preset?.id,
      // Retain the showEnvironmentBackground checkbox value when switching presets
      showEnvironmentBackground: lighting?.showEnvironmentBackground ?? false
    });
    setActivePreset(preset?.id);
  };

  const handleSliderChange = (field, value) => {
    onLightingChange({
      ...lighting,
      [field]: value,
      preset: 'custom' // Mark as custom when sliders are moved
    });
    setActivePreset('custom');
  };

  const handleEnvironmentBackgroundToggle = (checked) => {
    onLightingChange({
      ...lighting,
      showEnvironmentBackground: checked
    });
  };

  const handleReset = () => {
    const studioPreset = presets?.find(p => p?.id === 'studio');
    // Reset to default state with background off
    onLightingChange({
      ...studioPreset?.settings,
      preset: 'studio',
      showEnvironmentBackground: false
    });
    setActivePreset('studio');
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div className="absolute top-[476px] right-4 z-30 w-[218px] bg-white/[0.88] backdrop-blur-md rounded-lg border border-border shadow-floating max-h-[calc(100vh-32rem)] overflow-hidden flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-2 border-b border-border bg-card/50">
        <div className="flex items-center space-x-1.5">
          <Icon name="Sun" size={14} className="text-primary animate-pulse" />
          <h3 className="text-[10px] font-black uppercase tracking-tight text-foreground">Lighting & Env</h3>
        </div>
        <Button
          variant="ghost"
          size="sm"
          iconName="X"
          onClick={onToggle}
          className="w-5 h-5 p-0 flex items-center justify-center min-w-0 min-h-0 text-slate-400 hover:text-foreground"
        />
      </div>
      {/* Mode Toggle */}
      <div className="p-1.5 border-b border-border bg-card/30">
        <div className="flex items-center space-x-1">
          <button
            onClick={() => setAdvancedMode(false)}
            className={`flex-1 h-6 text-[9px] font-black uppercase tracking-wider rounded transition-colors ${
              !advancedMode ? 'bg-primary text-primary-foreground shadow-sm' : 'text-slate-400 hover:bg-slate-100 hover:text-foreground'
            }`}
          >
            Presets
          </button>
          <button
            onClick={() => setAdvancedMode(true)}
            className={`flex-1 h-6 text-[9px] font-black uppercase tracking-wider rounded transition-colors ${
              advancedMode ? 'bg-primary text-primary-foreground shadow-sm' : 'text-slate-400 hover:bg-slate-100 hover:text-foreground'
            }`}
          >
            Advanced
          </button>
        </div>
      </div>
      {/* Content */}
      <div className="overflow-y-auto flex-1 text-[10px]" style={{ maxHeight: 'calc(100vh - 36rem)' }}>
        {!advancedMode ? (
          // Presets View
          (<div className="p-2">
            <div className="grid grid-cols-2 gap-1.5">
              {presets?.map((preset) => (
                <button
                  key={preset?.id}
                  onClick={() => handlePresetChange(preset)}
                  className={`flex flex-col items-center p-2 rounded-md text-center transition-smooth border-2 ${
                    activePreset === preset?.id
                      ? 'bg-primary/10 border-primary' : 'bg-muted border-transparent hover:border-border hover:bg-muted/80'
                  }`}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center mb-1 ${
                    activePreset === preset?.id ? 'bg-primary/20' : 'bg-muted-foreground/10'
                  }`}>
                    <Icon 
                      name={preset?.icon} 
                      size={16} 
                      className={activePreset === preset?.id ? 'text-primary' : 'text-muted-foreground'}
                    />
                  </div>
                  <span className={`text-[10px] font-bold ${
                    activePreset === preset?.id ? 'text-foreground' : 'text-muted-foreground'
                  }`}>
                    {preset?.name}
                  </span>
                </button>
              ))}
            </div>
          </div>)
        ) : (
          // Advanced Controls View
          (<div className="p-2.5 space-y-2.5">
            {/* Light Intensity */}
            <div className="space-y-1">
              <div className="flex items-center justify-between text-[9px] font-bold uppercase text-slate-400">
                <span className="flex items-center space-x-1">
                  <Icon name="Sun" size={12} className="text-primary" />
                  <span>Intensity</span>
                </span>
                <span className="font-mono text-primary bg-primary/10 px-1 py-0.2 rounded">
                  {lighting?.intensity}%
                </span>
              </div>
              <input
                type="range"
                min="0"
                max="100"
                value={lighting?.intensity}
                onChange={(e) => handleSliderChange('intensity', parseInt(e?.target?.value))}
                className="w-full h-1 bg-gradient-to-r from-gray-400 to-yellow-400 rounded-lg appearance-none cursor-pointer"
              />
            </div>
            {/* Color Temperature */}
            <div className="space-y-1">
              <div className="flex items-center justify-between text-[9px] font-bold uppercase text-slate-400">
                <span className="flex items-center space-x-1">
                  <Icon name="Thermometer" size={12} className="text-accent" />
                  <span>Temperature</span>
                </span>
                <span className="font-mono text-accent bg-accent/10 px-1 py-0.2 rounded">
                  {lighting?.temperature}K
                </span>
              </div>
              <input
                type="range"
                min="2000"
                max="8000"
                step="100"
                value={lighting?.temperature}
                onChange={(e) => handleSliderChange('temperature', parseInt(e?.target?.value))}
                className="w-full h-1 bg-gradient-to-r from-orange-400 via-white to-blue-400 rounded-lg appearance-none cursor-pointer"
              />
            </div>
            {/* Shadow Softness */}
            <div className="space-y-1">
              <div className="flex items-center justify-between text-[9px] font-bold uppercase text-slate-400">
                <span className="flex items-center space-x-1">
                  <Icon name="Droplet" size={12} className="text-slate-400" />
                  <span>Shadows</span>
                </span>
                <span className="font-mono text-slate-500 bg-slate-100 px-1 py-0.2 rounded">
                  {lighting?.shadowSoftness}%
                </span>
              </div>
              <input
                type="range"
                min="0"
                max="100"
                value={lighting?.shadowSoftness}
                onChange={(e) => handleSliderChange('shadowSoftness', parseInt(e?.target?.value))}
                className="w-full h-1 bg-gradient-to-r from-gray-800 to-gray-300 rounded-lg appearance-none cursor-pointer"
              />
            </div>
            {/* Environment Lighting */}
            <div className="space-y-1">
              <div className="flex items-center justify-between text-[9px] font-bold uppercase text-slate-400">
                <span className="flex items-center space-x-1">
                  <Icon name="Globe" size={12} className="text-success" />
                  <span>Environment</span>
                </span>
                <span className="font-mono text-success bg-success/10 px-1 py-0.2 rounded">
                  {lighting?.environment}%
                </span>
              </div>
              <input
                type="range"
                min="0"
                max="100"
                value={lighting?.environment}
                onChange={(e) => handleSliderChange('environment', parseInt(e?.target?.value))}
                className="w-full h-1 bg-gradient-to-r from-gray-600 to-green-400 rounded-lg appearance-none cursor-pointer"
              />
            </div>
            {/* Ambient Occlusion */}
            <div className="space-y-1">
              <div className="flex items-center justify-between text-[9px] font-bold uppercase text-slate-400">
                <span className="flex items-center space-x-1">
                  <Icon name="Circle" size={12} className="text-purple-500" />
                  <span>Occlusion</span>
                </span>
                <span className="font-mono text-purple-500 bg-purple-500/10 px-1 py-0.2 rounded">
                  {lighting?.ambientOcclusion || 40}%
                </span>
              </div>
              <input
                type="range"
                min="0"
                max="100"
                value={lighting?.ambientOcclusion || 40}
                onChange={(e) => handleSliderChange('ambientOcclusion', parseInt(e?.target?.value))}
                className="w-full h-1 bg-gradient-to-r from-white to-purple-500 rounded-lg appearance-none cursor-pointer"
              />
            </div>
            {/* Additional Controls */}
            <div className="pt-2 border-t border-border space-y-1 text-[9px] font-bold uppercase text-slate-500">
              <label className="flex items-center justify-between cursor-pointer py-0.5">
                <span className="flex items-center space-x-1">
                  <Icon name="Image" size={12} className="text-primary" />
                  <span>BG Environment</span>
                </span>
                <input 
                  type="checkbox" 
                  className="rounded border-border w-3.5 h-3.5" 
                  checked={lighting?.showEnvironmentBackground ?? false}
                  onChange={(e) => handleEnvironmentBackgroundToggle(e?.target?.checked)}
                />
              </label>
              <label className="flex items-center justify-between cursor-pointer py-0.5">
                <span>HDR Map</span>
                <input type="checkbox" className="rounded border-border w-3.5 h-3.5" defaultChecked />
              </label>
              <label className="flex items-center justify-between cursor-pointer py-0.5">
                <span>Shadows En</span>
                <input type="checkbox" className="rounded border-border w-3.5 h-3.5" defaultChecked />
              </label>
            </div>
          </div>)
        )}
      </div>
      {/* Actions */}
      <div className="p-1.5 border-t border-border bg-card/30 flex justify-center">
        <button 
          onClick={handleReset}
          className="w-full h-7 text-[10px] font-black uppercase tracking-wider bg-slate-100 hover:bg-slate-200 text-slate-700 rounded transition-colors flex items-center justify-center space-x-1"
        >
          <Icon name="RotateCcw" size={12} />
          <span>Reset Env</span>
        </button>
      </div>
    </div>
  );
};

export default LightingControls;